import { useState } from 'react';
import { ErrorBanner } from '../FormBits.js';
import { Modal } from '../ui/modal.js';
import { fmtLimit } from '../PolicyControlTable.js';
import { policy } from './policyApi.js';
import {
  POLICY_DENOMINATOR_LABEL_HE, POLICY_MEASURE_BY_ID, policyComponentsLabelHe,
  type PolicyDocumentRead, type PolicyRuleProposal, type PolicyRuleRow, type PolicyRuleSaved,
} from '@agio/core/domain';

/** The rule in force that measures the same thing as the proposal — same measure, base and sleeves. */
const sameLine = (p: PolicyRuleProposal, r: PolicyRuleRow) =>
  r.effective_to === null && r.measure === p.measure && r.denominator === p.denominator &&
  (r.components ?? []).join() === (p.components ?? []).join();

/** A proposal the client already has as a rule still in force — re-reading the same document adds nothing. */
const sameRule = (p: PolicyRuleProposal, r: PolicyRuleRow) =>
  sameLine(p, r) && r.limit_min === p.limit_min && r.limit_max === p.limit_max;

/**
 * Upload the client's policy document (PDF, Excel or Word) and get its limits
 * back as proposed rules. Nothing is saved until the admin ticks the lines
 * that are right and presses save; each ticked line is then saved like a
 * hand-typed rule, so the server checks it the same way.
 */
export function PolicyDocumentDialog({ clientId, rules, onClose, onSaved }: {
  clientId: string; rules: PolicyRuleRow[]; onClose: () => void; onSaved: (stale: PolicyRuleSaved['stale']) => void;
}) {
  const existingRules = rules.length;
  const exists = (p: PolicyRuleProposal) => rules.some((r) => sameRule(p, r));
  // A new document with a different limit for a line the client has is a policy change of that line.
  const replaces = (p: PolicyRuleProposal) => (exists(p) ? undefined : rules.find((r) => sameLine(p, r)));
  const [file, setFile] = useState<File | null>(null);
  const [reading, setReading] = useState(false);
  const [read, setRead] = useState<PolicyDocumentRead | null>(null);
  const [ticked, setTicked] = useState<Set<number>>(new Set());
  const [from, setFrom] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const readFile = async () => {
    if (!file) return;
    setReading(true);
    setError(null);
    try {
      const r = await policy.readDocument(clientId, file);
      setRead(r);
      // A line the checks flagged, or one the client already has, starts unticked: the admin opts in after looking.
      setTicked(new Set(r.proposals.flatMap((p, i) => (p.warning_he || rules.some((x) => sameRule(p, x)) ? [] : [i]))));
      setFrom(r.effective_from ?? '');
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setReading(false);
    }
  };

  const save = async () => {
    if (!read) return;
    if (!from) { setError('יש לציין מאיזה חודש הכללים בתוקף'); return; }
    setSaving(true);
    setError(null);
    const picked = read.proposals.filter((_, i) => ticked.has(i));
    const stale = new Map<string, PolicyRuleSaved['stale'][number]>();
    try {
      // One at a time, in the document's order, so a failure says which line it was.
      for (const [n, p] of picked.entries()) {
        try {
          const old = replaces(p);
          const body = {
            label_he: p.label_he, measure: p.measure, components: p.components, denominator: p.denominator,
            limit_min: p.limit_min, limit_max: p.limit_max, fund_ids: old ? old.fund_ids : null,
            effective_from: from, effective_to: null, sort_order: old ? old.sort_order : existingRules + n,
          };
          if (old) for (const s of (await policy.changeRule(clientId, old.rule_id, body)).stale) stale.set(s.period, s);
          else await policy.createRule(clientId, body);
        } catch (e) {
          throw new Error(`"${p.label_he}": ${(e as Error).message}${n > 0 ? ` (${n} הכללים שלפניו נשמרו)` : ''}`, { cause: e });
        }
      }
      onSaved([...stale.values()].sort((a, b) => a.period.localeCompare(b.period)));
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setSaving(false);
    }
  };

  const toggle = (i: number, on: boolean) => {
    const next = new Set(ticked);
    if (on) next.add(i); else next.delete(i);
    setTicked(next);
  };

  return (
    <Modal
      open
      title="קריאת כללים ממסמך המדיניות"
      description="מעלים את מסמך מדיניות ההשקעות של הלקוח, והמערכת מציעה את הכללים שכתובים בו. שום דבר לא נשמר עד שתסמנו ותאשרו."
      onClose={onClose}
      size="lg"
      footer={
        <>
          <button type="button" onClick={onClose} className="rounded-md px-4 py-2 text-sm text-muted-foreground hover:bg-muted">ביטול</button>
          {read ? (
            <button type="button" disabled={saving || ticked.size === 0} onClick={save} className="rounded-md bg-primary px-5 py-2 text-sm font-semibold text-primary-foreground disabled:opacity-60">
              {saving ? 'שומר…' : `שמירת ${ticked.size === 1 ? 'כלל אחד' : `${ticked.size} כללים`}`}
            </button>
          ) : (
            <button type="button" disabled={!file || reading} onClick={readFile} className="rounded-md bg-primary px-5 py-2 text-sm font-semibold text-primary-foreground disabled:opacity-60">
              {reading ? 'קורא את המסמך…' : 'קריאת המסמך'}
            </button>
          )}
        </>
      }
    >
      <div className="grid gap-4">
        {!read && (
          <label className="grid gap-2 text-sm">
            <span className="text-muted-foreground">קובץ PDF, גיליון Excel או מסמך Word</span>
            <input
              type="file"
              accept=".pdf,.xlsx,.xls,.xlsm,.docx"
              disabled={reading}
              onChange={(e) => { setFile(e.target.files?.[0] ?? null); setError(null); }}
              className="text-sm"
            />
            {reading && <span className="text-xs text-muted-foreground">קריאה יסודית של המסמך לוקחת בדרך כלל דקה עד שלוש.</span>}
          </label>
        )}

        {read && (
          <>
            {read.proposals.length === 0 ? (
              <p className="rounded-lg border border-dashed border-border p-4 text-sm">לא נמצאו במסמך מגבלות שאפשר לבדוק אוטומטית.</p>
            ) : (
              <div className="grid gap-2">
                <p className="text-sm text-muted-foreground">
                  סמנו את הכללים הנכונים. {existingRules > 0 && `ללקוח כבר יש ${existingRules} כללים — המסומנים יתווספו לצידם.`}
                </p>
                {read.proposals.map((p, i) => (
                  <ProposalLine key={i} p={p} exists={exists(p)} replaces={replaces(p)} checked={ticked.has(i)} onChange={(on) => toggle(i, on)} />
                ))}
              </div>
            )}

            {read.unmapped.length > 0 && (
              <div className="grid gap-2 rounded-lg border border-border p-3 text-sm">
                <p className="font-semibold">מגבלות במסמך שהבקרה לא יכולה לבדוק</p>
                <ul className="grid gap-2">
                  {read.unmapped.map((u, i) => (
                    <li key={i}>
                      <Quote text={u.quote} />
                      <span className="block text-xs text-warning">{u.reason_he}</span>
                    </li>
                  ))}
                </ul>
              </div>
            )}

            {read.proposals.length > 0 && (
              <div className="grid gap-4 sm:grid-cols-2">
                <label className="grid gap-1 text-sm">
                  <span className="text-muted-foreground">בתוקף מחודש{read.effective_from ? ' (כפי שכתוב במסמך)' : ''}</span>
                  <input type="month" className="w-full rounded-md border border-border bg-popover px-3 py-2 text-sm" dir="ltr" value={from} onChange={(e) => setFrom(e.target.value)} />
                </label>
                <p className="self-end text-xs text-muted-foreground">הכללים יחולו על כל התיקים. אפשר לצמצם כלל לתיקים מסוימים בעריכה אחרי השמירה.</p>
              </div>
            )}
          </>
        )}
        <ErrorBanner error={error} title={read ? 'לא נשמר' : 'המסמך לא נקרא'} />
      </div>
    </Modal>
  );
}

function ProposalLine({ p, exists, replaces, checked, onChange }: {
  p: PolicyRuleProposal; exists: boolean; replaces: PolicyRuleRow | undefined; checked: boolean; onChange: (on: boolean) => void;
}) {
  const def = POLICY_MEASURE_BY_ID[p.measure];
  return (
    <label className={`flex items-start gap-3 rounded-lg border p-3 text-sm ${checked ? 'border-primary/50 bg-primary/5' : 'border-border'}`}>
      <input type="checkbox" className="mt-1" checked={checked} onChange={(e) => onChange(e.target.checked)} />
      <span className="grid min-w-0 gap-1">
        <span className="flex flex-wrap items-baseline gap-x-3 gap-y-1">
          <span className="font-semibold">{p.label_he}</span>
          <span className="tabular-nums">{fmtLimit({ unit: def.unit, limit_min: p.limit_min, limit_max: p.limit_max })}</span>
          <span className="text-xs text-muted-foreground">
            {p.components ? policyComponentsLabelHe(p.components) : def.labelHe}
            {def.unit === 'pct' ? ` · ${POLICY_DENOMINATOR_LABEL_HE[p.denominator]}` : ''}
          </span>
        </span>
        <Quote text={p.quote} />
        {exists && <span className="text-xs font-semibold text-muted-foreground">כלל זהה כבר קיים ללקוח</span>}
        {replaces && (
          <span className="text-xs font-semibold text-primary">
            שינוי מדיניות: מחליף את "{replaces.label_he}" ({fmtLimit({ unit: def.unit, limit_min: replaces.limit_min, limit_max: replaces.limit_max })}) מהחודש שבתוקף. החודשים שלפניו נשארים לפי הכלל הקודם.
          </span>
        )}
        {p.warning_he && <span className="text-xs font-semibold text-warning">{p.warning_he}</span>}
      </span>
    </label>
  );
}

/**
 * The line a rule was read from. A table row arrives as "cell | cell | cell";
 * each cell is isolated so a percentage or a "-A" cannot jump across the
 * row when Hebrew and numbers mix, and an empty cell shows as a dash.
 */
function Quote({ text }: { text: string }) {
  const cells = text.split('|').map((c) => c.trim());
  return (
    <span className="text-xs text-muted-foreground" dir="rtl">
      {cells.length === 1 ? <bdi>{text}</bdi> : cells.map((c, i) => (
        <span key={i}>
          {i > 0 && <span className="mx-2 opacity-50">│</span>}
          <bdi>{c || '—'}</bdi>
        </span>
      ))}
    </span>
  );
}
